import { useMemo } from 'react';

import { request } from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useQueries } from '@tanstack/react-query';

export type DashboardPeriod = 'day' | 'week' | 'month' | '3months';

export type OverviewTotals = {
  period: { label: string; key: string; from: string; to: string };
  totals: {
    orders: { value: number; prev: number; delta: number | null };
    suivi: { value: number; prev: number; delta: number | null };
    returnRate: { value: number; prev: number; delta: number | null };
    uniqueClients: { value: number };
    enterprises: { value: number };
  };
};

export type TimelinePoint = { date: string; orders: number; suivi: number };

export type Breakdown = {
  byEurekaStatus: { status: string; count: number }[];
  byRole: { role: string; count: number }[];
  byEnterprise: { enterpriseId: number; orders: number; suivi: number }[];
};

export type ProductsPayload = {
  topOrderedProducts: { productId: string; productName: string; count: number }[];
  topReturnedProducts: { productId: string; productName: string; count: number }[];
};

function statsRequest<T>(path: string, targetOrgId: string): Promise<T> {
  return request(path, {
    method: 'get',
    options: { headers: { 'x-organization': targetOrgId } },
    disableNotif: true,
  }) as Promise<T>;
}

export type UseDashboardStatsArgs = {
  /** Dynamo tenant id (stack suffix), not URL slug */
  targetOrgId: string;
  period: DashboardPeriod;
};

/**
 * Four independent analytics queries (overview, timeline, breakdown, products).
 * "Recent activity" is no longer here — it is derived from the first page of
 * `useDashboardOrders` / `useDashboardReturns` which also power the listing tabs.
 * This avoids a dedicated /recent API call.
 */
export function useDashboardStats({ targetOrgId, period }: UseDashboardStatsArgs) {
  const enabled = Boolean(targetOrgId);
  const periodQS = useMemo(() => encodeURIComponent(period), [period]);

  const sharedOpts = {
    enabled,
    /** Stats endpoints scan Dynamo — a single retry is plenty. */
    retry: 1,
  } as const;

  const [overviewQ, timelineQ, breakdownQ, productsQ] = useQueries({
    queries: [
      {
        ...sharedOpts,
        queryKey: ['dashboard-stats', 'overview', targetOrgId, period] as const,
        queryFn: () => {
          debug.log('Dashboard', `overview ${targetOrgId} ${period}`);
          return statsRequest<OverviewTotals>(
            `/stats/overview?period=${periodQS}`,
            targetOrgId,
          );
        },
      },
      {
        ...sharedOpts,
        queryKey: ['dashboard-stats', 'timeline', targetOrgId, period] as const,
        queryFn: () =>
          statsRequest<{ timeline: TimelinePoint[] }>(
            `/stats/timeline?period=${periodQS}`,
            targetOrgId,
          ),
      },
      {
        ...sharedOpts,
        queryKey: ['dashboard-stats', 'breakdown', targetOrgId, period] as const,
        queryFn: () =>
          statsRequest<Breakdown>(`/stats/breakdown?period=${periodQS}`, targetOrgId),
      },
      {
        ...sharedOpts,
        queryKey: ['dashboard-stats', 'products', targetOrgId, period] as const,
        queryFn: () =>
          statsRequest<ProductsPayload>(
            `/stats/products?period=${periodQS}`,
            targetOrgId,
          ),
      },
    ],
  });

  const refetch = () =>
    Promise.all([
      overviewQ.refetch(),
      timelineQ.refetch(),
      breakdownQ.refetch(),
      productsQ.refetch(),
    ]);

  return {
    overview: overviewQ.data,
    timeline: timelineQ.data?.timeline ?? [],
    breakdown: breakdownQ.data,
    products: productsQ.data,

    loading: {
      overview: overviewQ.isLoading,
      timeline: timelineQ.isLoading,
      breakdown: breakdownQ.isLoading,
      products: productsQ.isLoading,
    },
    errors: {
      overview: overviewQ.error,
      timeline: timelineQ.error,
      breakdown: breakdownQ.error,
      products: productsQ.error,
    },

    isFetchingAny:
      overviewQ.isFetching ||
      timelineQ.isFetching ||
      breakdownQ.isFetching ||
      productsQ.isFetching,

    refetch,
  };
}
